// Shared display formatters. The single place money is rendered, so the whole app is
// consistent. Uses the browser's locale (Intl default locale) and renders a proper
// currency when given a valid ISO 4217 code; otherwise falls back to a plain
// 2-decimal number with the code appended (covers custom/blank currency codes).

export function money(amount: number | null | undefined, currency?: string | null): string {
  const n = Number(amount) || 0;
  const cur = (currency || '').trim().toUpperCase();
  if (cur) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);
    } catch {
      /* not a valid ISO 4217 code — fall through to plain number + code */
    }
  }
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + (cur ? ` ${cur}` : '');
}

/** Compact relative time ("just now", "5m ago", "3h ago", "2d ago", else a date).
 *  Input is a UTC datetime string (SQLite `datetime('now')` form or ISO). */
export function relativeTime(value: string | null | undefined): string {
  if (!value) return '';
  // SQLite stores "YYYY-MM-DD HH:MM:SS" (UTC, no zone) — normalize to ISO UTC.
  const iso = value.includes('T') ? value : value.replace(' ', 'T') + 'Z';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
